import { useAuth } from 'wasp/client/auth';
import {
  updateCurrentUserNewsletterPreferences,
  initiateNewsletterSubscription,
} from 'wasp/client/operations';
import toast from 'react-hot-toast';
import { Button } from '../components/common';
import { useEffect, useState } from 'react';
import { type NewsProviderId, NEWS_PROVIDERS } from '../shared/newsProviders';
import {
  getNewsletterErrorMessage,
  getNewsletterPreferenceState,
  isNewsletterEligibleForSubscription,
  setNewsletterConsent,
  toggleNewsletterProviderSelection,
  type NewsletterPreferenceState,
} from './preferences';

export default function NewsletterPage() {
  const { data: user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [preferences, setPreferences] = useState<NewsletterPreferenceState>(
    () => getNewsletterPreferenceState(user),
  );

  useEffect(() => {
    setPreferences(getNewsletterPreferenceState(user));
  }, [user]);

  const hasSelectedProviders = preferences.newsletterProviders.length > 0;
  const isEmailVerified = isNewsletterEligibleForSubscription(user);
  const isActionDisabled =
    isUpdating ||
    !hasSelectedProviders ||
    (!preferences.sendNewsletter && !isEmailVerified);
  const actionLabel = preferences.sendNewsletter
    ? 'Unsubscribe'
    : 'Start Weekly Delivery';
  const description = preferences.sendNewsletter
    ? 'Manage your newsletter preferences below.'
    : hasSelectedProviders
      ? 'Your source selections are saved. Start weekly delivery whenever you are ready.'
      : 'Get weekly updates on the most inspiring and positive news stories.';

  const persistPreferences = async (
    nextPreferences: NewsletterPreferenceState,
    successMessage: string,
    fallbackErrorMessage: string,
  ) => {
    setIsUpdating(true);

    try {
      const updatedPreferences =
        await updateCurrentUserNewsletterPreferences(nextPreferences);
      setPreferences(getNewsletterPreferenceState(updatedPreferences));
      toast.success(successMessage);
    } catch (error) {
      toast.error(getNewsletterErrorMessage(error, fallbackErrorMessage));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProviderToggle = async (providerId: NewsProviderId) => {
    const nextPreferences = toggleNewsletterProviderSelection(
      preferences,
      providerId,
    );
    const provider = NEWS_PROVIDERS.find(
      (newsProvider) => newsProvider.id === providerId,
    );
    const providerName = provider?.name ?? providerId;
    const providerWasSelected =
      preferences.newsletterProviders.includes(providerId);

    await persistPreferences(
      nextPreferences,
      providerWasSelected
        ? `Removed ${providerName} from your newsletter sources.`
        : `Saved ${providerName} to your newsletter sources.`,
      'Failed to update newsletter preferences.',
    );
  };

  const handleSubscriptionToggle = async () => {
    if (preferences.sendNewsletter) {
      const nextPreferences = setNewsletterConsent(preferences, false);
      await persistPreferences(
        nextPreferences,
        'Weekly delivery is paused. Your source selections are still saved.',
        'Error updating newsletter subscription.',
      );
      setPendingConfirmation(false);
      return;
    }

    setIsUpdating(true);
    try {
      await initiateNewsletterSubscription({
        newsletterProviders: preferences.newsletterProviders,
      });
      setPendingConfirmation(true);
      toast.success(
        'Check your inbox to confirm your newsletter subscription.',
      );
    } catch (error) {
      toast.error(
        getNewsletterErrorMessage(error, 'Error starting newsletter delivery.'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <div className="space-y-8">
        {/* Header Section */}
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-950/30">
            <span className="text-3xl" role="img" aria-label="Newsletter">
              ✉️
            </span>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
            {preferences.sendNewsletter
              ? 'Newsletter Settings'
              : 'Your Weekly Dose of Good News'}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-300 max-w-md mx-auto">
            {description}
          </p>
        </div>

        {!isEmailVerified && (
          <div className="rounded-lg border border-warning-200 bg-warning-100/70 p-4 text-sm text-warning-700 dark:border-warning-900/40 dark:bg-warning-900/20 dark:text-warning-300">
            Verify your email before starting weekly delivery. Your saved source
            selections will stay ready in the meantime.
          </div>
        )}

        {pendingConfirmation && !preferences.sendNewsletter && (
          <div className="rounded-lg border border-warning-200 bg-warning-100/70 p-4 text-sm text-warning-700 dark:border-warning-900/40 dark:bg-warning-900/20 dark:text-warning-300">
            Check your inbox — one click to confirm your delivery.
          </div>
        )}

        {/* Provider Selection - Common Region & Figure/Ground */}
        <div className="bg-white dark:bg-boxdark rounded-lg p-6 shadow-xs space-y-6">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
            News Sources
          </h2>
          <div className="space-y-4">
            {NEWS_PROVIDERS.map((provider) => (
              <label
                key={provider.id}
                className={`
                  group block p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer
                  ${
                    preferences.newsletterProviders.includes(provider.id)
                      ? 'border-primary bg-primary/5 dark:border-primary/70 hover:bg-primary/10'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-primary/40 hover:bg-primary/[0.02] dark:hover:border-primary/30'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={preferences.newsletterProviders.includes(
                      provider.id,
                    )}
                    onChange={() => handleProviderToggle(provider.id)}
                    disabled={isUpdating}
                    aria-label={provider.name}
                    className="absolute h-5 w-5 opacity-0 peer"
                  />
                  <div
                    className={`
                      h-5 w-5 shrink-0 rounded border-2 transition-colors
                      peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-1
                      ${
                        preferences.newsletterProviders.includes(provider.id)
                          ? 'border-primary bg-primary group-hover:border-primary-600 group-hover:bg-primary-600'
                          : 'border-neutral-300 dark:border-neutral-600 group-hover:border-primary group-hover:bg-primary/10'
                      }
                    `}
                    aria-hidden="true"
                  >
                    {preferences.newsletterProviders.includes(provider.id) && (
                      <svg
                        viewBox="0 0 16 16"
                        fill="white"
                        className="h-full w-full"
                      >
                        <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{provider.icon}</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {provider.name}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      {provider.description}
                    </p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex flex-col items-center gap-3">
          <Button
            variant={preferences.sendNewsletter ? 'secondary' : 'primary'}
            onClick={handleSubscriptionToggle}
            disabled={isActionDisabled}
            className="min-w-[240px]"
          >
            {isUpdating ? 'Updating...' : actionLabel}
          </Button>
          {!preferences.sendNewsletter &&
            hasSelectedProviders &&
            !pendingConfirmation && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                Delivered every Monday morning
              </p>
            )}
        </div>
      </div>
    </div>
  );
}
